/**
 * AS-IS State Page
 * Main page for displaying AS-IS SEO state with summary cards and parameter lists
 */

import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';
import SummaryCards from './components/SummaryCards';
import ParameterTabs from './components/ParameterTabs';
import FilterChips from './components/FilterChips';
import ParameterList from './components/ParameterList';
import CompetitorSection from './components/CompetitorSection';
import LoadingSpinner from './components/LoadingSpinner';
import EmptyState from './components/EmptyState';
import './styles.css';
import { SEO_AS_IS_TABS } from '../../constants/telemetryModules';
import { usePageTracking } from '../../hooks/usePageTracking';

type TabType = 'onpage' | 'offpage' | 'technical';
type FilterType = 'all' | 'optimal' | 'needs_attention';

interface AsIsPageProps {
    userId?: string;
}

const extractSeoKeywords = (response: any): string[] => {
    const keywords = response?.data?.keywords;
    if (Array.isArray(keywords?.seo)) {
        return keywords.seo
            .filter((k: any) => k.is_selected || k.is_active)
            .map((k: any) => k.keyword)
            .filter(Boolean);
    }
    if (Array.isArray(keywords)) {
        return keywords
            .filter((k: any) => k.is_selected || k.is_active)
            .map((k: any) => k.keyword)
            .filter(Boolean);
    }
    return [];
};

const AsIsPage: React.FC<AsIsPageProps> = ({ userId: propUserId }) => {
    // Get user ID from props or localStorage
    const userId = propUserId || localStorage.getItem('user_id') || '';
    // Check both keys for backward compatibility (business_url is the actual key saved during onboarding)
    const siteUrl = localStorage.getItem('business_url') || localStorage.getItem('website_url') || '';

    // State
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [summary, setSummary] = useState<any>(null);
    const [parameters, setParameters] = useState<any>(null);
    const [competitors, setCompetitors] = useState<any>(null);

    // Filter state
    const [activeTab, setActiveTab] = useState<TabType>('onpage');
    const [statusFilter, setStatusFilter] = useState<FilterType>('all');
    usePageTracking({
        moduleName: 'SEO Manager',
        submoduleName: `AS-IS State / ${SEO_AS_IS_TABS[activeTab]}`,
        routePath: `/as-is#${activeTab}`,
    });

    // Tracked keywords and competitors from onboarding
    const [trackedKeywords, setTrackedKeywords] = useState<string[]>([]);
    const [competitorDomains, setCompetitorDomains] = useState<string[]>([]);

    // Load initial data
    useEffect(() => {
        if (userId && siteUrl) {
            loadData();
        } else {
            setError('Please complete onboarding first');
            setLoading(false);
        }
    }, [userId, siteUrl]);

    // Load parameters when tab or filter changes
    useEffect(() => {
        if (userId && siteUrl) {
            loadParameters();
        }
    }, [activeTab, statusFilter]);

    const loadData = async () => {
        setLoading(true);
        setError(null);

        try {
            // Load tracked keywords and competitors from profile
            const profile = await apiService.getUserProfile(userId);
            let keywords: string[] = [];
            let comps: string[] = [];
            try {
                const keywordUniverse = await apiService.getKeywordUniverse(userId);
                keywords = extractSeoKeywords(keywordUniverse);
            } catch (keywordErr) {
                console.warn('Could not load V2 SEO keywords, falling back to profile:', keywordErr);
            }
            if (profile) {
                if (!keywords.length) {
                    keywords = profile.selected_keywords || [];
                }
                setTrackedKeywords(keywords);
                const rawComps = profile.final_competitors || [];
                comps = rawComps.map((c: any) => c.domain || c);
                setCompetitorDomains(comps);
            }

            // Load summary data — pass keywords directly to avoid stale closure
            await loadSummary(keywords, comps);

            // Load parameters for current tab
            await loadParameters();

            // Load competitors
            await loadCompetitors(comps);

        } catch (err: any) {
            console.error('Error loading AS-IS data:', err);
            setError(err.message || 'Failed to load AS-IS data');
        } finally {
            setLoading(false);
        }
    };

    const loadSummary = async (keywords?: string[], comps?: string[]) => {
        try {
            const result = await apiService.getAsIsSummary({
                user_id: userId,
                site_url: siteUrl,
                tracked_keywords: keywords ?? trackedKeywords,
                competitors: comps ?? competitorDomains
            });
            setSummary(result?.data);
        } catch (err) {
            console.error('Error loading summary:', err);
        }
    };

    const loadParameters = async () => {
        try {
            const filterParam = statusFilter === 'all' ? undefined : statusFilter;
            const result = await apiService.getAsIsParameters({
                user_id: userId,
                site_url: siteUrl,
                tab: activeTab,
                status_filter: filterParam
            });
            setParameters(result?.data);
        } catch (err) {
            console.error('Error loading parameters:', err);
        }
    };

    const loadCompetitors = async (comps?: string[]) => {
        try {
            const domains = comps ?? competitorDomains;
            if (domains.length > 0) {
                const result = await apiService.getAsIsCompetitors({
                    user_id: userId,
                    site_url: siteUrl,
                    competitors: domains
                });
                setCompetitors(result?.data);
            }
        } catch (err) {
            console.error('Error loading competitors:', err);
        }
    };

    const handleRefresh = async () => {
        setLoading(true);
        try {
            // Re-fetch profile first so we always use current universe keywords
            const profile = await apiService.getUserProfile(userId);
            let keywords: string[] = [];
            let comps: string[] = [];
            try {
                const keywordUniverse = await apiService.getKeywordUniverse(userId);
                keywords = extractSeoKeywords(keywordUniverse);
            } catch (keywordErr) {
                console.warn('Could not load V2 SEO keywords, falling back to profile:', keywordErr);
            }
            if (profile) {
                if (!keywords.length) {
                    keywords = profile.selected_keywords || [];
                }
                setTrackedKeywords(keywords);
                const rawComps = profile.final_competitors || [];
                comps = rawComps.map((c: any) => c.domain || c);
                setCompetitorDomains(comps);
            }
            await apiService.refreshAsIsData({
                user_id: userId,
                site_url: siteUrl,
                tracked_keywords: keywords,
                competitors: comps
            });
            await loadSummary(keywords, comps);
            await loadParameters();
            await loadCompetitors(comps);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleTabChange = (tab: TabType) => {
        setActiveTab(tab);
    };

    const handleFilterChange = (filter: FilterType) => {
        setStatusFilter(filter);
    };

    if (loading) {
        return (
            <div className="asis-page">
                <LoadingSpinner message="Loading AS-IS State..." />
            </div>
        );
    }

    if (error) {
        return (
            <div className="asis-page">
                <EmptyState
                    title="Error Loading Data"
                    message={error}
                    actionLabel="Retry"
                    onAction={loadData}
                />
            </div>
        );
    }

    return (
        <div className="asis-page">
            {/* Header */}
            <header className="asis-header">
                <div className="asis-header-content">
                    <h1>AS-IS State</h1>
                    <div className="asis-header-subtitle-row">
                        <p className="asis-subtitle">Current SEO performance snapshot</p>
                        {summary && (
                            <span className={`data-source-badge ${summary.from_cache ? 'source-db' : 'source-api'}`}>
                                {summary.from_cache ? '💾 Loaded from Database' : '⚡ Live Data'}
                            </span>
                        )}
                    </div>
                </div>
                <button className="refresh-btn" onClick={handleRefresh} disabled={loading}>
                    <span className="refresh-icon">↻</span>
                    Refresh Data
                </button>
            </header>

            {/* Summary Cards */}
            <section className="asis-summary">
                <SummaryCards userId={userId} data={summary} />
            </section>

            {/* Parameters Section */}
            <section className="asis-parameters">
                <div className="parameters-header">
                    <h2>Performance Parameters</h2>
                    <div className="parameters-filters">
                        <ParameterTabs activeTab={activeTab} onTabChange={handleTabChange} />
                        <FilterChips activeFilter={statusFilter} onFilterChange={handleFilterChange} />
                    </div>
                </div>

                <ParameterList
                    parameters={parameters?.parameters || []}
                    summary={parameters?.summary}
                    tab={activeTab}
                    userId={userId}
                />
            </section>

            {/* Competitors Section */}
            <section className="asis-competitors">
                <CompetitorSection
                    data={competitors}
                    emptyState={!competitorDomains.length}
                />
            </section>
        </div>
    );
};

export default AsIsPage;
